import { AddMember, TeamMember } from 'components/account/StoreDetails';
import { ButtonCTASmall, Skeleton } from 'components/ui';
import { format, parseISO } from 'date-fns';
import querystring from 'querystring';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetStoreInfoQuery,
  useGetUserInfoQuery,
} from 'store/services/segmentsApi';

/**
 *
 * @param {{storeName:string}} param0
 * @returns
 */
export default function StoreDetails({ storeName }) {
  const { data: userInfo } = useGetUserInfoQuery();

  const queryParams = querystring.stringify({
    store_name: storeName,
  });

  const { data: storeInfo, isLoading } = useGetStoreInfoQuery(queryParams, {
    skip: !!!storeName,
  });

  const [addMemberOpen, setAddMemberOpen] = useState(false);

  const queryParam = `store_name=${storeName}`;

  const isUserAdmin =
    storeInfo?.users
      .find((user) => user.email === userInfo?.email)
      ?.role_name.toLowerCase() === 'admin';

  const maxSeats = (() => {
    const level = storeInfo?.active_sub.level.toLowerCase();

    const limits = {
      free: 1,
      'small business': 2,
      growth: 5,
    };

    if (level === 'enterprise' || level === 'unlimited') return 'Unlimited';

    // @ts-ignore
    return limits[level] + ' max';
  })();

  return (
    <div className="bg-white border shadow-sm mt-9 rounded-2xl">
      <div className="p-4 lg:p-8 bg-segments-accent-color-1 bg-opacity-5 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <div className="grid grid-flow-col auto-cols-max gap-x-4 lg:gap-x-8">
            <Skeleton isLoaded={!!!isLoading}>
              <img
                src={storeInfo?.img || '/assets/logo-shopify.png'}
                height="96"
                width="96"
                alt="store logo"
                className="self-center"
              />
            </Skeleton>

            <div className="flex flex-col justify-between">
              <div>
                <Skeleton isLoaded={!!!isLoading}>
                  <h3 className="text-xl leading-7 truncate md:text-2xl">
                    {storeInfo?.name || storeInfo?.store_name || '--'}
                  </h3>
                </Skeleton>
              </div>
              <div>
                <Skeleton isLoaded={!!!isLoading}>
                  <p className="text-black text-opacity-50 text-segments-13px">
                    {storeInfo?.plan_name || storeInfo?.platform_name || '--'}
                  </p>
                </Skeleton>
              </div>
              <Link
                to={{
                  pathname: '/dashboard',
                  search: queryParam,
                }}
                className="text-segments-13px text-segments-link"
              >
                View Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="px-8 mt-10">
        <div className="flex items-end justify-between">
          <p className="text-base font-bold">Subscription</p>
          <div>
            <Link
              className="w-full px-4 py-2.5 text-base font-bold text-white border rounded-lg text-segments-13px focus:outline-none bg-segments-accent-color-1 hover:bg-segments-dark flex space-x-1.5"
              to={{
                pathname: '/account/change-plan',
                search: queryParam,
              }}
            >
              Change plan
            </Link>
          </div>
        </div>
        <table className="w-full mt-6">
          <thead className="text-segments-13px">
            <tr className="border-b border-black">
              <th className="pb-4 text-left">Current Plan</th>
              <th className="pb-4 text-left">Start date</th>
              <th className="pb-4 text-left">End date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr>
              <td className="py-2 text-base font-normal">
                {storeInfo?.active_sub?.level
                  ? storeInfo.active_sub.level
                  : '--'}
              </td>
              <td className="text-base font-normal">
                {storeInfo?.active_sub?.start_date
                  ? format(
                      parseISO(storeInfo.active_sub.start_date),
                      'yyyy-MM-dd'
                    )
                  : '--'}
              </td>
              <td className="text-base font-normal">
                {storeInfo?.active_sub?.end_date
                  ? format(
                      parseISO(storeInfo.active_sub.end_date),
                      'yyyy-MM-dd'
                    )
                  : '--'}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="flex justify-between md:items-center mt-14">
          <div>
            <p className="text-base font-bold">Team Members</p>
            <div className="md:space-x-4 md:flex text-segments-13px">
              <p className="text-black text-opacity-30">Seats: {maxSeats}</p>
              <Link
                className="flex space-x-1 font-bold text-segments-link"
                to={{
                  pathname: '/account/change-plan',
                  search: queryParam,
                }}
              >
                Change plan
              </Link>
            </div>
          </div>
          <div>
            <ButtonCTASmall
              variant="main"
              type="button"
              onClick={() => {
                setAddMemberOpen(!!!addMemberOpen);
              }}
              disabled={!!!isUserAdmin}
              title={
                !!!isUserAdmin ? 'Only Admin can add a member' : 'Add a member'
              }
            >
              <p className="px-6 font-normal text-segments-13px">
                Add a Member
              </p>
            </ButtonCTASmall>
          </div>
        </div>
        <AddMember
          open={addMemberOpen}
          setOpen={setAddMemberOpen}
          storeName={storeName}
        />
        <table className="w-full my-6">
          <thead className="text-segments-13px">
            <tr className="border-b border-black">
              <th className="pb-4 text-left">Name</th>
              <th className="hidden pb-4 text-left md:block">Email</th>
              <th className="pb-4 text-left">Role</th>
              <th className="pb-4 text-left">Delete</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {storeInfo?.users.map((member) => (
              <TeamMember
                member={member}
                isMemberAdmin={isUserAdmin}
                key={member.id}
                storeName={storeName}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
